import config from '../config.json'

import * as watchUtils from "@arcgis/core/core/watchUtils";

import Graphic from "@arcgis/core/Graphic";
import WebMap from "@arcgis/core/WebMap";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import Basemap from "@arcgis/core/Basemap";

import { ListaResumenCapa, ElementoListaResumenCapaAgrupacion, ElementoListaResumenCapaGraphic } from "../classes/reducerClasses";
import { AtributosGraphicsMapa } from './enums';
import { getFieldValueWithDomain } from './esriUtils';
import { ConfiguracionWidget } from "../classes/widgetClasses";

export function urlTableroEsValida(url){
    const inicioCorrecto = "https://app.powerbi.com";
    return url?.substring(0, (inicioCorrecto).length).toUpperCase() === inicioCorrecto.toLocaleUpperCase();
}

export function getValConfigVisoresIfDefault(valorConfigVisores, parametros, propDefaults){
    if (valorConfigVisores.trim().toUpperCase() === config.defaultKeyWordVisores.toUpperCase()){
        return parametros[propDefaults];
    } else {
        return valorConfigVisores;
    }
}

export function getValorTablaParametros(tipo, valor) {
    if (tipo === config.parametrosTiposValor.int){
        return parseInt(valor);
    } else if (tipo === config.parametrosTiposValor.string) {
        return valor;
    } else if (tipo === config.parametrosTiposValor.float) {
        return parseFloat(valor);
    } else if (tipo === config.parametrosTiposValor.bool) {
        return !!valor
    }
}

function getBaseMap(parametros) {
    const { idsVectors, portal, titulo, thumbnailUrl } = config.parametrosCodigos.mapaBase
    const baseLayers = parametros[idsVectors].split(',').map(id => new VectorTileLayer({ portalItem: { id: id, portal: { url: parametros[portal] } } }));
    return new Basemap({
        title: parametros[titulo],
        thumbnailUrl: parametros[thumbnailUrl],
        baseLayers: baseLayers,
        spatialReference: new SpatialReference({wkid: 3857})
    })
}

export function getWebMap(webMapId, parametros){
    const webMap = new WebMap({ portalItem: { id: webMapId, portal: { url: config.urlPortal } } });
    if (parametros[config.parametrosCodigos.mapaBase.portal] && parametros[config.parametrosCodigos.mapaBase.idsVectors]){
        webMap.basemap = getBaseMap(parametros);
    }
    return webMap;
}

export function getLayerViewOfLayer(mapView, idLayer){
    return mapView.layerViews.find(x => x.layer.id === idLayer);
}

export async function getLayerViewGeometriesByIds(mapView, layerView, ids){
    const query = getQueryLayerViewOfListasResumen(mapView, layerView);
    query.outFields = [];
    query.objectIds = ids;
    return new Promise(resolve => {
        layerView.queryFeatures(query).then(({ features }) => {
            resolve(features);
        });
    });
}

function ejecutarFunciones(funciones, argsFunciones){
    funciones.forEach((funcion, index) => {
        funcion(...argsFunciones[index]);
    })
}

export function ejecutarFuncionesWhenLayersUpdated(layerViews, funciones, argsFunciones){
    const layerViewsUpdating = layerViews.filter(x => x.updating);
    if (layerViewsUpdating.length > 0){
        const promisesUpdated = [];
        layerViewsUpdating.forEach(layerViewUpdating => {
            promisesUpdated.push(new Promise(resolve => {
                watchUtils.whenFalseOnce(layerViewUpdating, "updating", () => {
                    resolve();
                })
            }));
        });
        Promise.all(promisesUpdated).then(() =>{
            ejecutarFunciones(funciones, argsFunciones)
        });
    } else {
        ejecutarFunciones(funciones, argsFunciones)
    }
}

export function removeGraphicsMapViewSegunListaResumen(mapView, tipoListaResumen){
    const graphicsBorrar = mapView.graphics.filter(x => x.attributes[AtributosGraphicsMapa.tipo] === tipoListaResumen);
    mapView.graphics.removeMany(graphicsBorrar);
}

export function getFeatureLayers(map){
    return map.layers.items.filter(x => x.type === "feature");
}

export async function loadWebMapAndLayers(parametrosVisor){
    const webMap = new WebMap({ portalItem: { id: parametrosVisor.webMapId, portal: { url: config.urlPortal } } });
    const promiseWebMap = webMap.load();
    await promiseWebMap;
    const promisesLoadLayers = getFeatureLayers(webMap).map(layer => layer.load());
    const promiseLoadLayers = Promise.all(promisesLoadLayers);
    await promiseLoadLayers;
    return webMap;
}

export function getParametrosWidget(layer, codigoWidget){
    if (layer.popupTemplate?.expressionInfos) {
        const expressionInfo =  layer.popupTemplate.expressionInfos.find(x => x.title.startsWith(codigoWidget));
        if (expressionInfo) {
            const partes = expressionInfo.title.split(',');
            return new ConfiguracionWidget(expressionInfo, partes.slice(1));
        } else {
            return null;
        }
    } else {
        return null;
    } 
}

export function campoExiste(layer, nombreCampo){
    //El layer debe estar loaded
    return layer.fields.some(x => x.name === nombreCampo); 
}

export function getListaResumenBase(layer){
    const configuracionWidget = getParametrosWidget(layer, config.codigosWidgets.listaResumen);
    let listaResumenAux = null;
    if (configuracionWidget){
        const { expressionInfo, parametrosAdicionales } = configuracionWidget;
        listaResumenAux = new ListaResumenCapa(layer.id, expressionInfo);
        let campoOrden = null;
        let campoAgrupacion = null;
        let zoomParaPuntoString = null;
        if(layer.geometryType === "point"){
            [zoomParaPuntoString, campoOrden, campoAgrupacion] = parametrosAdicionales;
        } else {
            [campoOrden, campoAgrupacion] = parametrosAdicionales;
        }
        if (zoomParaPuntoString){
            const zoomParaPunto = parseInt(zoomParaPuntoString);
            if (!isNaN(zoomParaPunto)){
                listaResumenAux.zoomParaPunto = zoomParaPunto;
            }
        }
        if (campoOrden && campoExiste(layer, campoOrden)) {
            listaResumenAux.campoOrden = campoOrden;
        }
        if (campoAgrupacion && campoExiste(layer, campoAgrupacion)){
            listaResumenAux.campoAgrupacion = campoAgrupacion;
        }
    }
    return listaResumenAux;
}

export async function getListaOfListaResumenCapa(query, listaResumen, layerOrLayerView, codigoController){
    const nombreParaAbortController = "controllerQueryElementos_" + codigoController;
    const promise = new Promise(resolve => {
        var controller = new AbortController();
        var signal = controller.signal;
        if (layerOrLayerView[nombreParaAbortController]){
            layerOrLayerView[nombreParaAbortController].abort();
        }
        layerOrLayerView[nombreParaAbortController] = controller;
        if (listaResumen.campoOrden){
            query.orderByFields = [listaResumen.campoOrden];
            if (listaResumen.campoAgrupacion && listaResumen.campoAgrupacion !== listaResumen.campoOrden){
                query.orderByFields.unshift(listaResumen.campoAgrupacion);
            }
        }
        layerOrLayerView.queryFeatures(query, { signal }).then(({ features: graphics }) => {
            layerOrLayerView[nombreParaAbortController] = null;
            const listaAux = [];
            let indexGeneral = 0;
            let elementoAgrupacionActual = null;
            const layerAsociado = layerOrLayerView.type === "feature" ? layerOrLayerView : layerOrLayerView.layer;
            if (graphics.length > 0){
                if (listaResumen.campoAgrupacion){
                    elementoAgrupacionActual = new ElementoListaResumenCapaAgrupacion(getFieldValueWithDomain(layerAsociado, listaResumen.campoAgrupacion, graphics[0].attributes[listaResumen.campoAgrupacion], true), indexGeneral + 1);
                    listaAux.push(elementoAgrupacionActual);
                }
                graphics.forEach((graphic) => {
                    graphic.popupTemplate = {
                        content: [{
                            type: "text",
                            text: `{expression/${listaResumen.expressionInfo.name}}`
                        }],
                        expressionInfos: [listaResumen.expressionInfo]
                    }
                    const valorSiDominio = getFieldValueWithDomain(layerAsociado, listaResumen.campoAgrupacion, graphic.attributes[listaResumen.campoAgrupacion], true);
                    if (listaResumen.campoAgrupacion && elementoAgrupacionActual.valor !== valorSiDominio){
                        elementoAgrupacionActual.indexFin = indexGeneral; //Antes de crear el nuevo, crea el indexFin del actual
                        indexGeneral += 1;
                        elementoAgrupacionActual = new ElementoListaResumenCapaAgrupacion(valorSiDominio, indexGeneral + 1); //Es + 1 porque corresponde al siguiente por agregar **
                        listaAux.push(elementoAgrupacionActual);
                    }
                    indexGeneral += 1; //**
                    listaAux.push(new ElementoListaResumenCapaGraphic(listaResumen.campoAgrupacion ? false : true, false, graphic, graphic.attributes[layerAsociado.objectIdField]));
                })
                if (listaResumen.campoAgrupacion){
                    elementoAgrupacionActual.indexFin = indexGeneral; //Para el último grupo
                }
            }
            resolve(listaAux);
        }).catch(e => {
            if (e.name !== "AbortError"){
                resolve(null);
                console.error(e);
            }
        });
    });
    return promise;
}

export function getListasResumenBase(webMap){
    //El webMap de estar loaded al igual que los layers asociados
    const listasResumen = [];
    getFeatureLayers(webMap).forEach(layer => {
        const listaResumenAux = getListaResumenBase(layer);
        if(listaResumenAux){
            listasResumen.push(listaResumenAux);
        }
    })
    return listasResumen;
}

export function getQueryLayerViewOfListasResumen(mapView, layerView){
    const query = layerView.createQuery();
    query.outSpatialReference = mapView.map.basemap.spatialReference;
    query.geometry = mapView.extent;
    return query;
}

function actualizarNumRegistrosLayerEnLayerList(layerList, layer, numRegistros){
    const layerListItem = layerList.operationalItems.find(x => x.layer.id === layer.id); 
    layerListItem.title =  layer.title + (layerListItem.visibleAtCurrentScale ? " (" + numRegistros + ")" : "");
    layerListItem.conteoActual = numRegistros; //Propiedad a la medida
}

export function calculoConteosEnLayerList(mapView, layerViews, layerList){
    layerViews.forEach(layerView => {
        const query = getQueryLayerViewOfListasResumen(mapView, layerView);
        layerView.queryFeatureCount(query).then(count => {
            const { layer } = layerView;
            actualizarNumRegistrosLayerEnLayerList(layerList, layer, count);
        }); 
    })
}

export function addGraphicSublayer(mapView, geometry){
    const graphic = new Graphic(getGraphicBusqueda(geometry, mapView.highlightOptions.color));
    graphic.setAttribute(AtributosGraphicsMapa.ofSublayer, true);
    removeGraphicsSublayer(mapView);
    mapView.graphics.add(graphic);
}

export function removeGraphicsSublayer(mapView){
    const graphicsBorrar = mapView.graphics.filter(x => x.attributes[AtributosGraphicsMapa.ofSublayer]);
    mapView.graphics.removeMany(graphicsBorrar);
}

export function addGraphicListaResumen(mapView, geometry, tipoListaResumen){
    const graphic = new Graphic(getGraphicBusqueda(geometry, config.colorGraphicsListaResumen));
    graphic.setAttribute(AtributosGraphicsMapa.tipo, tipoListaResumen)
    mapView.graphics.removeAll();
    mapView.graphics.add(graphic);
}

export function addManyGraphicsListaResumen(mapView, graphics, tipoListaResumen){
    const graphicsToAdd = graphics.map(graphic => {
        const newGraphic = new Graphic(getGraphicBusqueda(graphic.geometry, config.colorGraphicsListaResumen));
        newGraphic.setAttribute(AtributosGraphicsMapa.tipo, tipoListaResumen) //Permite identificar los graphics del mapa de que tipo son para después borrarlos por ejemplo
        return newGraphic;
    });
    mapView.graphics.removeAll();
    mapView.graphics.addMany(graphicsToAdd);
}

export function getGraphicBusqueda(geometry, colorMatch) {
    let resp = null;
    if (geometry.type === "point"){
        resp = {
            geometry: geometry,
            symbol: {
                type: "simple-marker",
                style: "square",
                size: "15px",
                color: [0, 0, 0, 0],
                outline: {
                    color: colorMatch,
                    width: 2
                }
            }
        }
    } else if(geometry.type === "polygon"){
        resp = {
            geometry: geometry,
            symbol: {
                type: "simple-fill",
                style: "none",
                outline: {
                    color: colorMatch,
                    width: 2
                }
            }
        }
    } else if (geometry.type === "polyline"){
        resp = {
            geometry: geometry,
            symbol: {
                type: "simple-line",
                color: colorMatch,
                width: 2
            }
        }
    }
    return resp;
}